import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsChipClickable, BdsIcon, BdsTooltip } from '@/core/constants/webComponents';
import { applyBdsChipExpandableStyles } from '@/core/utils/bdsChipStyles';
import { normalizeString as normalize } from '@/core/utils/normalizeString';
import { useFilterSelectorKeyboard } from '@/core/utils/useFilterSelectorKeyboard';
import { useChipCloseEvents } from '@/core/utils/useChipCloseEvents';
import { useClickOutside } from '@/core/hooks/useClickOutside';
import { checkAllSelected } from '@/core/hooks/useSelectionAnalysis';
import { useDropdownAutoScroll } from '@/core/hooks/useDropdownAutoScroll';
import { type SelectionMap, type SelectionState } from './types';
import './FilterSelector.scss';

type TranslateFunction = ReturnType<typeof useTranslation>['t'];

const SELECT_ALL_KEY = '__select_all__';

function getNextSelectionState(
  current: SelectionState | null,
  allowNegation: boolean,
): SelectionState | null {
  if (!allowNegation) return current === 'add' ? null : 'add';
  if (current === null) return 'add';
  if (current === 'add') return 'remove';
  return null;
}

function getAllToggleTarget(
  isAllAdd: boolean,
  isAllRemove: boolean,
  allowNegation: boolean,
): SelectionState | null {
  if (allowNegation) {
    if (isAllAdd) return 'remove';
    if (isAllRemove) return null;
    return 'add';
  }
  return isAllAdd ? null : 'add';
}

const NegationIcon: React.FC = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.99967 1.33301C9.78034 1.33301 11.4546 2.02647 12.7138 3.28562C13.9728 4.54478 14.6663 6.21897 14.6663 7.99967C14.6663 9.78034 13.9728 11.4546 12.7138 12.7138C11.4546 13.9728 9.78034 14.6663 7.99967 14.6663C6.21897 14.6663 4.54478 13.9728 3.28562 12.7138C2.02647 11.4546 1.33301 9.78034 1.33301 7.99967C1.33301 6.21897 2.02647 4.54478 3.28562 3.28562C4.54478 2.02647 6.21897 1.33301 7.99967 1.33301ZM7.99967 13.6247C11.1013 13.6247 13.6247 11.1013 13.6247 7.99967C13.6247 4.89807 11.1013 2.37467 7.99967 2.37467C4.89807 2.37467 2.37467 4.89807 2.37467 7.99967C2.37467 11.1013 4.89807 13.6247 7.99967 13.6247ZM5.13509 8.52102H10.8643V7.47935H5.13509V8.52102Z" fill="#E60F0F" />
  </svg>
);

export interface FilterSelectorProps {
  label: string;
  placeholder: string;
  availableItems: Array<string | FilterSelectorOption>;
  selection: SelectionMap;
  onChange: React.Dispatch<React.SetStateAction<SelectionMap>>;
  tooltipText?: string;
  allowNegation?: boolean;
  disabled?: boolean;
  isHighlighted?: boolean;
  singleSelect?: boolean;
  hideSelectAllWhenSingleItem?: boolean;
}

export interface FilterSelectorOption {
  key: string;
  label: string;
  description?: string;
  iconPlatform?: string;
}

interface NormalizedFilterSelectorOption {
  key: string;
  label: string;
  description?: string;
  iconPlatform?: string;
  searchableText: string;
}

function checkNoSearchResults(
  options: NormalizedFilterSelectorOption[],
  search: string,
  filtered: NormalizedFilterSelectorOption[],
): boolean {
  return options.length > 0 && search.trim().length > 0 && filtered.length === 0;
}

function buildItemSelection(
  prev: SelectionMap,
  item: string,
  singleSelect: boolean,
  allowNegation: boolean,
): SelectionMap {
  if (singleSelect) return prev[item] === 'add' ? {} : { [item]: 'add' };
  const next = getNextSelectionState(prev[item] ?? null, allowNegation);
  const updated = { ...prev };
  if (next === null) delete updated[item];
  else updated[item] = next;
  return updated;
}

function buildToggleAllSelection(
  prev: SelectionMap,
  options: NormalizedFilterSelectorOption[],
  allowNegation: boolean,
): SelectionMap {
  const isAllAdd = options.length > 0 && options.every((o) => prev[o.key] === 'add');
  const isAllRemove = options.length > 0 && options.every((o) => prev[o.key] === 'remove');
  const target = getAllToggleTarget(isAllAdd, isAllRemove, allowNegation);
  const updated = { ...prev };
  if (target === null) options.forEach((o) => delete updated[o.key]);
  else options.forEach((o) => { updated[o.key] = target; });
  return updated;
}

function normalizeItem(item: string | FilterSelectorOption): NormalizedFilterSelectorOption {
  if (typeof item === 'string') return { key: item, label: item, searchableText: item };
  const description = item.description?.trim();
  return {
    key: item.key,
    label: item.label,
    description,
    iconPlatform: item.iconPlatform,
    searchableText: description ? `${item.label} ${description}` : item.label,
  };
}

function getOptionState(
  isSelectAll: boolean,
  key: string,
  selection: SelectionMap,
  allAdd: boolean,
  allRemove: boolean,
): SelectionState | null {
  if (!isSelectAll) return selection[key] ?? null;
  if (allAdd) return 'add';
  if (allRemove) return 'remove';
  return null;
}

function buildContainerClass(disabled: boolean, isOpen: boolean): string {
  const classes = ['cf-selector'];
  if (disabled) classes.push('cf-selector--disabled');
  if (isOpen) classes.push('cf-selector--open');
  return classes.join(' ');
}

function buildFieldClass(isOpen: boolean, isHighlighted: boolean, disabled: boolean): string {
  const classes = ['cf-selector__field'];
  if (isOpen) classes.push('cf-selector__field--open');
  if (isHighlighted) classes.push('cf-selector__field--highlighted');
  if (disabled) classes.push('cf-selector__field--disabled');
  return classes.join(' ');
}

function buildArrowClass(isOpen: boolean): string {
  return `cf-selector__arrow${isOpen ? ' cf-selector__arrow--open' : ''}`;
}

function buildSearchInputClass(isHighlighted: boolean): string {
  return `cf-selector__search-input${isHighlighted ? ' cf-selector__search-input--highlighted' : ''}`;
}

function buildDropdownOptions(params: {
  singleSelect: boolean;
  hideSelectAllWhenSingleItem: boolean;
  normalizedOptions: NormalizedFilterSelectorOption[];
  isFiltered: boolean;
  filtered: NormalizedFilterSelectorOption[];
  t: TranslateFunction;
}) {
  const { singleSelect, hideSelectAllWhenSingleItem, normalizedOptions, isFiltered, filtered, t } = params;
  
  const shouldShowSelectAll = !singleSelect && !(hideSelectAllWhenSingleItem && normalizedOptions.length === 1);
  const selectAllLabel = isFiltered 
    ? t('multipleEdition.selectAllFiltered', 'Todos filtrados') 
    : t('multipleEdition.selectAll', 'Todos');
  
  const selectAllOption = shouldShowSelectAll ? [{
    key: SELECT_ALL_KEY,
    label: selectAllLabel,
    isSelectAll: true as const,
    description: undefined,
    iconPlatform: undefined,
  }] : [];
  
  const regularOptions = filtered.map((option) => ({
    key: option.key,
    label: option.label,
    description: option.description,
    iconPlatform: option.iconPlatform,
    isSelectAll: false as const,
  }));
  
  return [...selectAllOption, ...regularOptions];
}

export const FilterSelector: React.FC<FilterSelectorProps> = ({
  label,
  placeholder,
  availableItems,
  selection,
  onChange,
  tooltipText,
  allowNegation = true,
  disabled = false,
  isHighlighted = false,
  singleSelect = false,
  hideSelectAllWhenSingleItem = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [lastFocusedIndex, setLastFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef<(HTMLElement | null)[]>([]);
  const chipWrapperRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const listRef = useRef<HTMLUListElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const outsideRefs = useMemo(() => [fieldRef, dropdownRef], []);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setLastFocusedIndex(-1);
  }, []);

  useClickOutside(outsideRefs, closeDropdown, isOpen);

  const normalizedOptions = useMemo<NormalizedFilterSelectorOption[]>(
    () => (Array.isArray(availableItems) ? availableItems : []).map(normalizeItem),
    [availableItems],
  );

  const optionByKey = useMemo(
    () => new Map(normalizedOptions.map((option) => [option.key, option])),
    [normalizedOptions],
  );

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    return q
      ? normalizedOptions.filter((option) => normalize(option.searchableText).includes(q))
      : normalizedOptions;
  }, [normalizedOptions, search]);

  const hasNoSearchResults = checkNoSearchResults(normalizedOptions, search, filtered);

  const optionKeys = useMemo(() => normalizedOptions.map((o) => o.key), [normalizedOptions]);
  const allAdd = checkAllSelected(optionKeys, selection, 'add');
  const allRemove = checkAllSelected(optionKeys, selection, 'remove');

  const isFiltered = search.trim().length > 0;
  const filteredKeys = useMemo(() => filtered.map((o) => o.key), [filtered]);
  const filteredAllAdd = checkAllSelected(filteredKeys, selection, 'add');
  const filteredAllRemove = checkAllSelected(filteredKeys, selection, 'remove');

  const handleToggleItem = useCallback(
    (item: string) => {
      onChange((prev) => buildItemSelection(prev, item, singleSelect, allowNegation));
      // Keep the search text so multiple matching options can be selected; return focus to input
      requestAnimationFrame(() => {
        searchRef.current?.focus();
      });
    },
    [onChange, allowNegation, singleSelect],
  );

  const handleToggleAll = useCallback(
    () => {
      onChange((prev) => buildToggleAllSelection(prev, normalizedOptions, allowNegation));
      // Keep the search text so multiple matching options can be selected; return focus to input
      requestAnimationFrame(() => {
        searchRef.current?.focus();
      });
    },
    [onChange, normalizedOptions, allowNegation],
  );

  const handleToggleFilteredAll = useCallback(
    () => {
      onChange((prev) => buildToggleAllSelection(prev, filtered, allowNegation));
      // Keep the search text so multiple matching options can be selected; return focus to input
      requestAnimationFrame(() => {
        searchRef.current?.focus();
      });
    },
    [onChange, filtered, allowNegation],
  );

  const handleRemoveItem = useCallback((item: string) => {
    onChange((prev) => {
      const updated = { ...prev };
      delete updated[item];
      return updated;
    });
    // Return focus to input after removing a chip
    requestAnimationFrame(() => {
      searchRef.current?.focus();
    });
  }, [onChange]);

  const handleRemoveAll = useCallback(() => {
    onChange((prev) => {
      const updated = { ...prev };
      normalizedOptions.forEach((option) => delete updated[option.key]);
      return updated;
    });
    // Return focus to input after removing all chips
    requestAnimationFrame(() => {
      searchRef.current?.focus();
    });
  }, [onChange, normalizedOptions]);

  const availableSet = useMemo(() => new Set(normalizedOptions.map((option) => option.key)), [normalizedOptions]);
  const selectionEntries = useMemo(
    () => Object.entries(selection).filter(([key]) => availableSet.has(key)),
    [selection, availableSet],
  );
  useDropdownAutoScroll(isOpen, dropdownRef, selectionEntries.length);

  useEffect(() => {
    chipRefs.current.forEach((chipEl) => {
      if (chipEl) applyBdsChipExpandableStyles(chipEl);
    });
  }, [selectionEntries, allAdd, allRemove]);

  useEffect(() => {
    const el = chipsContainerRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [selectionEntries.length]);

  // Prevent focus loss when clicking on dropdown elements (except input)
  useEffect(() => {
    const el = dropdownRef.current;
    if (!el || !isOpen) return;

    const preventFocusLoss = (e: MouseEvent) => {
      const target = e.target;
      if (target instanceof Element && target.closest('input')) {
        return; // Allow clicks on input
      }
      e.preventDefault(); // Prevent focus loss on other dropdown elements
    };

    el.addEventListener('mousedown', preventFocusLoss);
    return () => el.removeEventListener('mousedown', preventFocusLoss);
  }, [isOpen]);

  useChipCloseEvents({ 
    allAdd, 
    allRemove, 
    chipRefs, 
    selectionEntries, 
    handleRemoveAll, 
    handleRemoveItem,
    hideSelectAllWhenSingleItem,
    optionKeysLength: optionKeys.length,
  });

  const { handleTriggerKeyDown, handleChipKeyDown } = useFilterSelectorKeyboard({
    disabled,
    isOpen,
    setIsOpen,
    setSearch,
    searchRef,
    chipWrapperRefs,
    listRef,
    filtered,
    handleToggleItem,
    handleToggleAll,
    handleRemoveItem,
    handleRemoveAll,
    lastFocusedIndex,
    setLastFocusedIndex,
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setIsOpen(true);
    setLastFocusedIndex(-1); // Reset when search changes, as filtered list may be different
  };

  const handleSearchInputClick = useCallback(() => {
    if (!isOpen && !disabled) setIsOpen(true);
  }, [isOpen, disabled]);

  const handleArrowClick = useCallback(() => {
    if (!disabled) setIsOpen((o) => !o);
  }, [disabled]);

  const dropdownOptions = useMemo(() => 
    buildDropdownOptions({
      singleSelect,
      hideSelectAllWhenSingleItem,
      normalizedOptions,
      isFiltered,
      filtered,
      t,
    }),
    [singleSelect, hideSelectAllWhenSingleItem, normalizedOptions, isFiltered, filtered, t]
  );

  const navigateToNextOption = useCallback((currentIndex: number) => {
    const buttons = Array.from(listRef.current?.querySelectorAll('button') ?? []);
    if (currentIndex + 1 < buttons.length) {
      buttons[currentIndex + 1].focus();
    }
  }, [listRef]);

  const navigateToPreviousOption = useCallback((currentIndex: number) => {
    if (currentIndex === 0) {
      searchRef.current?.focus();
    } else {
      const buttons = Array.from(listRef.current?.querySelectorAll('button') ?? []);
      buttons[currentIndex - 1].focus();
    }
  }, [listRef, searchRef]);

  const selectOption = useCallback((index: number, isSelectAll: boolean) => {
    if (isSelectAll) {
      // Use filtered toggle if search is active, otherwise toggle all
      if (isFiltered) {
        handleToggleFilteredAll();
      } else {
        handleToggleAll();
      }
      return;
    }
    const optionIndex = singleSelect || (hideSelectAllWhenSingleItem && normalizedOptions.length === 1) 
      ? index 
      : index - 1;
    if (optionIndex >= 0 && optionIndex < filtered.length) {
      handleToggleItem(filtered[optionIndex].key);
    }
  }, [isFiltered, handleToggleFilteredAll, handleToggleAll, handleToggleItem, filtered, singleSelect, hideSelectAllWhenSingleItem, normalizedOptions.length]);

  const handleOptionKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, index: number, isSelectAll: boolean) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setLastFocusedIndex(index + 1);
      navigateToNextOption(index);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setLastFocusedIndex(index - 1);
      navigateToPreviousOption(index);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      setLastFocusedIndex(index);
      selectOption(index, isSelectAll);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setLastFocusedIndex(-1);
      closeDropdown();
      searchRef.current?.focus();
    }
  }, [navigateToNextOption, navigateToPreviousOption, selectOption, closeDropdown, searchRef]);

  const arrowLabel = isOpen 
    ? t('globalFilter.closeOptions', 'Fechar opções') 
    : t('globalFilter.openOptions', 'Abrir opções');

  return (
    
    <div className={buildContainerClass(disabled, isOpen)} ref={containerRef}>
      <span className="cf-selector__label">
        {label}
        {tooltipText && (
          <BdsTooltip position="top-left" tooltip-text={tooltipText}>
            <BdsIcon name="info" size="x-small" theme="outline" />
          </BdsTooltip>
        )}
      </span>
      <div
        ref={fieldRef}
        className={buildFieldClass(isOpen, isHighlighted, disabled)}
      >
        <div className="cf-selector__trigger-content">
          <div className="cf-selector__chips" ref={chipsContainerRef}>
            {(allAdd || allRemove) && !(hideSelectAllWhenSingleItem && optionKeys.length === 1) ? (
              <button
                type="button"
                ref={(el) => { chipWrapperRefs.current[0] = el; }}
                className="cf-selector__chip-wrapper"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => handleChipKeyDown(e, SELECT_ALL_KEY, 0, true)}
              >
                <BdsChipClickable
                  ref={(el: HTMLElement | null) => { chipRefs.current[0] = el; }}
                  color="outline"
                  {...(!disabled && { close: true })}
                >
                  <span className="cf-selector__chip-content">
                    {allRemove && <NegationIcon />}
                    <span className="cf-selector__chip-text">{t('multipleEdition.selectAll', 'Todos')}</span>
                  </span>
                </BdsChipClickable>
              </button>
            ) : (
              selectionEntries.map(([key, state], index) => (
                <button
                  key={key}
                  type="button"
                  ref={(el) => { chipWrapperRefs.current[index] = el; }}
                  className="cf-selector__chip-wrapper"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => handleChipKeyDown(e, key, index, false)}
                >
                  <BdsChipClickable
                    ref={(el: HTMLElement | null) => { chipRefs.current[index] = el; }}
                    color="outline"
                    {...(!disabled && { close: true })}
                  >
                    <span className="cf-selector__chip-content">
                      {state === 'remove' && <NegationIcon />}
                      <span className="cf-selector__chip-text">{optionByKey.get(key)?.label || key}</span>
                    </span>
                  </BdsChipClickable>
                </button>
              ))
            )}
            <input
              ref={searchRef}
              className={buildSearchInputClass(isHighlighted)}
              value={search}
              onChange={handleSearchChange}
              onKeyDown={disabled ? undefined : handleTriggerKeyDown}
              placeholder={selectionEntries.length === 0 ? placeholder : ''}
              onClick={handleSearchInputClick}
              aria-haspopup="true"
              disabled={disabled}
            />
          </div>
        </div>
        <button
          type="button"
          tabIndex={-1}
          className={buildArrowClass(isOpen)}
          onClick={handleArrowClick}
          aria-label={arrowLabel}
          disabled={disabled}
        >
          <BdsIcon name="arrow-down" size="x-small" />
        </button>
      </div>
      {isOpen && (
        <div className="cf-selector__dropdown" ref={dropdownRef}>
          {/* NOSONAR — role="listbox" on <ul> is the correct ARIA pattern for a custom multi-select; a native <select> cannot implement three-state (null/add/remove) selection */}
          <ul className="cf-selector__list" ref={listRef} role="listbox" tabIndex={-1}> {/* NOSONAR */}
            {normalizedOptions.length === 0 || hasNoSearchResults ? (
              <li className="cf-selector__option cf-selector__option--empty">
                <span className="cf-selector__option-text">
                  {hasNoSearchResults
                    ? t('globalFilter.noResultsFound', 'Nenhum resultado encontrado')
                    : t('globalFilter.noOptions', 'Sem opções disponíveis')}
                </span>
              </li>
            ) : (
              dropdownOptions.map(({ key, label: itemLabel, description, iconPlatform, isSelectAll }, index) => {
                // Use filtered state when search is active, otherwise use global state
                const displayAllAdd = isFiltered ? filteredAllAdd : allAdd;
                const displayAllRemove = isFiltered ? filteredAllRemove : allRemove;
                const state = getOptionState(isSelectAll, key, selection, displayAllAdd, displayAllRemove);
                const isLastFocused = lastFocusedIndex === index;
                const checkboxClass = `cf-selector__checkbox cf-selector__checkbox--${state ?? 'empty'}`;
                const optionBtnClass = isLastFocused 
                  ? 'cf-selector__option-btn cf-selector__option-btn--keyboard-focused' 
                  : 'cf-selector__option-btn';
                const handleClick = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (isSelectAll) {
                    // Use filtered toggle if search is active, otherwise toggle all
                    if (isFiltered) {
                      handleToggleFilteredAll();
                    } else {
                      handleToggleAll();
                    }
                  } else {
                    handleToggleItem(key);
                  }
                };
                return (
                  <li key={key} className="cf-selector__option">
                    <button
                      type="button"
                      className={optionBtnClass}
                      onClick={handleClick}
                      onKeyDown={(e) => handleOptionKeyDown(e, index, isSelectAll)}
                      onFocus={() => setLastFocusedIndex(index)}
                      aria-pressed={state !== null}
                    >
                      <span className={checkboxClass}>
                        {state === 'add' && <span className="cf-selector__check-icon">✓</span>}
                        {state === 'remove' && <span className="cf-selector__check-icon">−</span>}
                      </span>
                      {iconPlatform && (
                        <BdsIcon
                          theme="outline"
                          type="logo"
                          name={iconPlatform}
                          size="small"
                        />
                      )}
                      <span className="cf-selector__option-text-wrapper">
                        <span className="cf-selector__option-text">{itemLabel}</span>
                        {description && (
                          <span className="cf-selector__option-subtext">{description}</span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};